<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <FancyServerTable
      v-if="isStateLoaded"
      ref="fancyTableRef"
      :data="usersTableData"
      :columns="usersTableHeaders"
      :enable-filters="true"
      :enable-pagination="true"
      :enable-column-visibility="true"
      :enable-column-pinning="true"
      :initial-column-visibility="currentSavedState?.columnVisibility || {}"
      :initial-column-order="currentSavedState?.columnOrder || []"
      :initial-column-sizing="currentSavedState?.columnSizing || {}"
      :initial-column-filters="currentSavedState?.columnFilters || []"
      :initial-sorting="currentSavedState?.sorting ?? []"
      :initial-column-pinning="
        currentSavedState?.columnPinning || {
          left: ['email'],
          right: ['actions'],
        }
      "
      :total-rows="totalRows"
      :page-index="pageIndex"
      :page-size="pageSize"
      :auto-filter="true"
      enable-row-selection
      :get-row-id="row => String(row.id)"
      @update:row-selection="handleRowSelectionChange"
      :loading="isLoading"
      @update:state="debouncedHandleTableStateChange"
      @fetch-data="fetchUsers"
    >
      <template #config-manager>
        <FancyTableConfigManager
          :saved-configs="savedTableConfigs"
          :current-config="currentSavedState"
          :is-loading="false"
          :can-save-state="canSaveState"
          @save-config="handleSaveConfig"
          @update-config="handleUpdateConfig"
          @apply-config="handleApplyConfig"
          @delete-config="handleDeleteConfig"
          @mark-as-default="handleMarkAsDefault"
        />
      </template>
      <template #actions>
        <UsersTableBulkActions
          :selectedUsers="selectedUsers"
          v-if="selectedUsers.length > 1"
          @reloadUsersTable="getUsersTableList"
        />
      </template>
    </FancyServerTable>
  </div>
</template>

<script setup lang="ts">
import { h, onMounted, ref } from 'vue';
import type { UserInList } from '@/_helpers/interfaces';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/stores/user.store';
import {
  FancyServerTable,
  type FancyTableColumnDef,
  type ServerTableOptions,
  useTableRenderers,
  FieldType,
  FilterType,
  FancyTableConfigManager,
} from '@/components/shared/fancyTables';

import UsersTableRowActions from './UsersTableRowActions.vue';
import UsersTableBulkActions from './UsersTableBulkActions.vue';
import { useFancyTableConfig } from '@/_helpers/composables/fancy-table-config.composable';

const selectedUsers = ref<UserInList[]>([]);
// Cache to store all selected row data across pages
const selectedRowsCache = ref<Map<string, any>>(new Map());
const fancyTableRef = ref<any>(null);
const { GetFieldRenderer } = useTableRenderers();

const { getUsersList } = useUserStore();
const { t } = useI18n();

const props = defineProps<{
  customerId: number;
}>();

// Map filters to API params
const mapFiltersToApiParams = (filters: any[]) => {
  const params: any = {};

  filters.forEach(filter => {
    const { id, value } = filter;

    switch (id) {
      case 'first_name':
        if (value) params.first_name = value;
        break;
      case 'last_name':
        if (value) params.last_name = value;
        break;
      case 'email':
        if (value) params.email = value;
        break;
      case 'created_on':
        if (value?.from) params.created_after = value.from;
        if (value?.to) params.created_before = value.to;
        break;
      case 'active':
        if (value !== undefined && value !== null) params.active = value;
        break;
      default:
        break;
    }
  });

  return params;
};

// Map sorting to API param
const mapSortingToApiParam = (sorting: any[]): string => {
  if (sorting.length === 0) return '';

  const sort = sorting[0];
  const { id, desc } = sort;
  const backendField = id; // Fields map directly (active handled by backend UserOrderingFilter)

  return desc ? `-${backendField}` : backendField;
};

// Server-side fetch function
const fetchUsers = async (options?: ServerTableOptions) => {
  isLoading.value = true;

  try {
    const currentPageIndex = options?.pageIndex ?? 0;
    const currentPageSize = options?.pageSize ?? 10;
    const filters = options?.filters ?? [];
    const sorting = options?.sorting ?? [];

    // Update pagination state
    pageIndex.value = currentPageIndex;
    pageSize.value = currentPageSize;

    // Map filters to API params
    const filterParams = mapFiltersToApiParams(filters);

    // Map sorting to API param
    const orderingParam = mapSortingToApiParam(sorting);

    const apiParams = {
      page: currentPageIndex + 1,
      page_size: currentPageSize,
      customer_id: props.customerId,
      ...filterParams,
      ...(orderingParam && { ordering: orderingParam }),
    };

    const response = await getUsersList(apiParams);

    usersTableData.value = response.results;
    totalRows.value = response.count;
  } catch (error) {
    console.error('Error fetching users:', error);
    usersTableData.value = [];
    totalRows.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const isLoading = ref(false);
const usersTableData = ref<UserInList[]>([]);
const totalRows = ref(0);
const pageIndex = ref(0);
const pageSize = ref(10);

const usersTableHeaders: FancyTableColumnDef<UserInList>[] = [
  {
    header: t('users.titles.first_name'),
    accessorKey: 'first_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 200,
  },
  {
    header: t('users.titles.last_name'),
    accessorKey: 'last_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 200,
  },
  {
    header: t('common.titles.email'),
    accessorKey: 'email',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: false,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 300,
  },
  {
    header: t('users.forms.created_at'),
    enableSorting: true,
    accessorKey: 'created_on',
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 200,
  },
  {
    header: t('users.titles.active'),
    accessorKey: 'active',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.BOOLEAN,
    filterType: FilterType.BOOLEAN,
    filterFn: 'boolean',
    cell: GetFieldRenderer(FieldType.BOOLEAN),
    size: 150,
  },
  {
    header: t('common.headers.actions'),
    accessorKey: 'actions',
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false,
    hideFromColumnConfig: true,
    cell: info => {
      return h(UsersTableRowActions, {
        key: `actions-${info.row.original.id}-${info.row.original.active}`,
        user: info.row.original,
        onReloadTable: getUsersTableList,
      });
    },
  },
];

const getUsersTableList = async () => {
  // This is now a wrapper for fetchUsers for backward compatibility
  // Reset selection after data reload to clear deleted rows from selection
  selectedUsers.value = [];
  selectedRowsCache.value.clear();

  // Reset the table's internal row selection state
  if (fancyTableRef.value) {
    fancyTableRef.value.resetRowSelection();
  }

  await fetchUsers();
};

const {
  savedTableConfigs,
  currentSavedState,
  canSaveState,
  isStateLoaded,
  handleSaveConfig,
  handleUpdateConfig,
  handleApplyConfig,
  handleDeleteConfig,
  handleMarkAsDefault,
  debouncedHandleTableStateChange,
} = useFancyTableConfig({
  tableName: 'users',
  fancyTableRef: fancyTableRef,
  tableLoader: getUsersTableList,
});

const handleRowSelectionChange = (selection: any[]) => {
  // Update cache: add newly selected rows from current page
  selection.forEach(row => {
    selectedRowsCache.value.set(String(row.id), row);
  });

  // Remove deselected rows: find rows in current page that are no longer selected
  const currentPageIds = new Set(usersTableData.value.map(row => String(row.id)));
  const selectedIds = new Set(selection.map(row => String(row.id)));

  currentPageIds.forEach(id => {
    if (!selectedIds.has(id)) {
      selectedRowsCache.value.delete(id);
    }
  });

  // Update selectedUsers with all cached rows
  selectedUsers.value = Array.from(selectedRowsCache.value.values());
};

onMounted(async () => {
  await getUsersTableList();
});
</script>
